import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { environment } from 'O3_environment';
import { ClientDiagnosticsService } from '../services/client-diagnostics.service';

export const CORRELATION_ID_HEADER = 'X-Correlation-Id';

function originOf(url: string): string | null {
  try {
    return new URL(url, window.location.origin).origin;
  } catch {
    return null;
  }
}

/**
 * Origins that belong to our own API, derived from `environment.apiUrls`.
 *
 * Scoped deliberately rather than stamping every outgoing request:
 *
 *  - A custom header turns an otherwise simple cross-origin request into one
 *    that needs a CORS preflight. `apiUrls` also contains third-party
 *    endpoints — `cnas-wpf` is a desktop bridge on `localhost:5180` — which
 *    have no reason to allow `X-Correlation-Id` and would start failing.
 *  - The header is only *useful* against core-api, which is what adopts it as
 *    `audit_log.request_id`. Sending it anywhere else leaks a client
 *    identifier for no diagnostic gain.
 *
 * Everything served by core-api (`core`, `ops`, and each module) shares the
 * `core` origin, so that origin is the filter; entries pointing elsewhere fall
 * out automatically and no allow-list needs hand-maintaining.
 */
const apiOrigins: ReadonlySet<string> = (() => {
  const coreOrigin = originOf(environment.apiUrls['core'] ?? '');
  const origins = new Set<string>();

  if (coreOrigin) {
    for (const url of Object.values(environment.apiUrls ?? {})) {
      const origin = originOf(url);
      if (origin && origin === coreOrigin) origins.add(origin);
    }
  }

  return origins;
})();

/**
 * Mints a per-request correlation id and sends it as `X-Correlation-Id` to our
 * own API.
 *
 * The server's `AuditContextInterceptor` adopts an inbound `x-correlation-id`
 * as its `requestId`, which is what lands in the indexed `audit_log.request_id`
 * column. That makes the id the *join key* between a ticket and the server-side
 * trace of the request that failed — pasting it into the audit log's
 * `requestId` filter returns that request's server-side history. No new log
 * store and no new read API are involved.
 *
 * Composes with `includeBearerTokenInterceptor` rather than fighting it: this
 * one only adds a header and observes the error channel, so the bearer token
 * and any Keycloak-driven retry are untouched.
 *
 * On failure it records **metadata only** — method, masked URL, status. Never
 * request or response bodies: in a HIS that is exactly where PHI lives.
 */
export const correlationIdInterceptor: HttpInterceptorFn = (req, next) => {
  const requestOrigin = originOf(req.url);
  if (!requestOrigin || !apiOrigins.has(requestOrigin)) {
    return next(req);
  }

  const diagnostics = inject(ClientDiagnosticsService);
  const correlationId = crypto.randomUUID();

  const withCorrelationId = req.clone({
    setHeaders: { [CORRELATION_ID_HEADER]: correlationId },
  });

  return next(withCorrelationId).pipe(
    catchError((error: unknown) => {
      diagnostics.recordFailedRequest({
        correlationId,
        method: req.method,
        maskedUrl: ClientDiagnosticsService.maskUrl(req.url),
        status: error instanceof HttpErrorResponse ? error.status : 0,
        at: new Date().toISOString(),
      });
      return throwError(() => error);
    }),
  );
};
